import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TorPreviewDocument } from "@/components/governance/TorPreviewDocument";
import { TorApprovalPanel } from "@/components/governance/TorApprovalPanel";
import { TorVersionHistory } from "@/components/governance/TorVersionHistory";
import { TorCommentsSection } from "@/components/governance/TorCommentsSection";
import { useGovernance } from "@/hooks/useGovernance";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

export default function TorReview() {
  const { committeeId } = useParams();
  const { profile } = useAuth();
  const { toast } = useToast();
  const { committees, terms, members, markTorApproved } = useGovernance();

  const committee = useMemo(
    () => committees.find((item) => item.id === committeeId),
    [committees, committeeId],
  );
  const tor = useMemo(() => terms.find((item) => item.committeeId === committeeId), [terms, committeeId]);

  if (!committee || !tor) {
    return (
      <Card className="shadow-card">
        <CardContent className="py-16 text-center text-muted-foreground">
          ToR review view is not available.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-3xl font-medium text-foreground">Review Terms of Reference</h1>
        <p className="text-muted-foreground">{committee.name}</p>
      </div>

      <Tabs defaultValue="preview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="preview">ToR Preview</TabsTrigger>
          <TabsTrigger value="approval">Approval Log</TabsTrigger>
          <TabsTrigger value="versions">Version History</TabsTrigger>
          <TabsTrigger value="comments">Comments</TabsTrigger>
        </TabsList>

        <TabsContent value="preview">
          <TorPreviewDocument committee={committee} tor={tor} members={members} />
        </TabsContent>

        <TabsContent value="approval">
          <TorApprovalPanel
            tor={tor}
            members={members}
            currentUserId={profile?.id ?? "anonymous"}
            onApprove={(comment) => {
              if (!profile?.id) return;
              markTorApproved(tor.id, profile.id, comment);
              toast({
                title: "Approval Recorded",
                description: "Your approval has been added to the ToR approval log.",
              });
            }}
          />
        </TabsContent>

        <TabsContent value="versions">
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle>Version History</CardTitle>
            </CardHeader>
            <CardContent>
              <TorVersionHistory tor={tor} members={members} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="comments">
          <TorCommentsSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}
